import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { STATUS_COLORS } from '../../constants/map';
import celfinetLogo from '../../../../assets/Cyient_Slate_Std_RGB.png';

const MapLegend = ({ stats, total, colourMode, onColourModeChange, featureStats, hideInactive, onHideInactiveChange, activityStatus, onActivityStatusChange, tileLayer, onTileLayerChange, selectedFeatures, showTacPolygons, onTacPolygonsChange, collapsed: collapsedProp, onCollapsedChange }) => {
  const [collapsedInternal, setCollapsedInternal] = useState(false);
  const collapsed = collapsedProp !== undefined ? collapsedProp : collapsedInternal;
  const setCollapsed = onCollapsedChange || setCollapsedInternal;

  // Collapsed state — small clickable pill that re-opens the panel
  if (collapsed) {
    return (
      <Box onClick={function() { setCollapsed(false); }}
        sx={{
          backgroundColor: 'rgba(255,255,255,0.97)', borderRadius: 2,
          px: 1.2, py: 0.9, border: '1px solid #CBD5E0',
          fontFamily: '"IBM Plex Mono", monospace',
          cursor: 'pointer', pointerEvents: 'all', alignSelf: 'flex-start',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', gap: 1,
          transition: 'all 0.15s',
          '&:hover': { borderColor: '#1565C0' },
          '&:hover .burger': { backgroundColor: '#1565C0' },
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <Box className="burger" sx={{ width: 14, height: 2, backgroundColor: '#64748B', borderRadius: 1 }} />
          <Box className="burger" sx={{ width: 14, height: 2, backgroundColor: '#64748B', borderRadius: 1 }} />
          <Box className="burger" sx={{ width: 14, height: 2, backgroundColor: '#64748B', borderRadius: 1 }} />
        </Box>
        <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Menu
        </Typography>
      </Box>
    );
  }

  return (
  <Box sx={{
    backgroundColor: 'rgba(255,255,255,0.97)', borderRadius: 2,
    px: 2, py: 1.5, border: '1px solid #CBD5E0',
    fontFamily: '"IBM Plex Mono", monospace',
    minWidth: 160, pointerEvents: 'all',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  }}>

    {/* Header with minimize button */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8, pb: 0.8, borderBottom: '1px solid #E2E8F0' }}>
      <Typography sx={{ color: '#1565C0', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
        Controls
      </Typography>
      <Box onClick={function() { setCollapsed(true); }}
        title="Minimizar"
        sx={{
          cursor: 'pointer', width: 18, height: 18, borderRadius: 1, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid #CBD5E0', transition: 'all 0.15s',
          '&:hover': { borderColor: '#1565C0' },
          '&:hover .minbar': { backgroundColor: '#1565C0' },
        }}>
        <Box className="minbar" sx={{ width: 9, height: 2, backgroundColor: '#64748B', borderRadius: 1 }} />
      </Box>
    </Box>

    {/* Map Layer selector */}
    <Box sx={{ mb: 0.8, pb: 0.8, borderBottom: '1px solid #E2E8F0' }}>
      <Typography sx={{ color: '#94A3B8', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', mb: 0.5 }}>
        Map Layer
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[
          { id: 'light',     label: 'Light'     },
          { id: 'dark',      label: 'Dark'      },
          { id: 'satellite', label: 'Satellite' },
        ].map(function(layer) {
          var active = tileLayer === layer.id;
          return (
            <Box key={layer.id} onClick={function() { onTileLayerChange(layer.id); }}
              sx={{
                flex: 1, textAlign: 'center', cursor: 'pointer', borderRadius: 1, py: 0.4, px: 0.5,
                backgroundColor: active ? '#1565C0' : 'transparent',
                border: active ? '1px solid #1565C0' : '1px solid #CBD5E0',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#1565C0' },
              }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: active ? '#fff' : '#64748B', letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                {layer.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>

    {/* Colour By toggle */}
    <Box sx={{ mb: 0.8, pb: 0.8, borderBottom: '1px solid #E2E8F0' }}>
      <Typography sx={{ color: '#94A3B8', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', mb: 0.5 }}>
        Colour By
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
      {['status', 'feature'].map(function(mode) {
        var active = colourMode === mode;
        return (
          <Box key={mode} onClick={function() { onColourModeChange(mode); }}
            sx={{
              flex: 1, textAlign: 'center', cursor: 'pointer',
              px: 1, py: 0.4, borderRadius: 1, fontSize: 9, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: 'inherit',
              backgroundColor: active ? '#1565C0' : 'transparent',
              color: active ? '#fff' : '#64748B',
              border: '1px solid ' + (active ? '#1565C0' : '#CBD5E0'),
              transition: 'all 0.15s',
              '&:hover': { borderColor: '#1565C0', color: '#1565C0' },
            }}>
            {mode === 'status' ? 'Status' : 'Type'}
          </Box>
        );
      })}
      </Box>
    </Box>

    {/* Activity Status selector */}
    <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid #E2E8F0' }}>
      <Typography sx={{ color: '#94A3B8', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', mb: 0.5 }}>
        Activity
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
      {['Open', 'Closed'].map(function(s) {
        var active = activityStatus === s;
        return (
          <Box key={s} onClick={function() { onActivityStatusChange(s); }}
            sx={{
              flex: 1, textAlign: 'center', cursor: 'pointer',
              px: 1, py: 0.4, borderRadius: 1, fontSize: 9, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: 'inherit',
              backgroundColor: active ? '#1565C0' : 'transparent',
              color: active ? '#fff' : '#64748B',
              border: '1px solid ' + (active ? '#1565C0' : '#CBD5E0'),
              transition: 'all 0.15s',
              '&:hover': { borderColor: '#1565C0', color: '#1565C0' },
            }}>
            {s}
          </Box>
        );
      })}
      </Box>
    </Box>

    {/* TAC Polygons toggle */}
    <Box onClick={function() { onTacPolygonsChange(!showTacPolygons); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 0.8, pb: 0.8,
        borderBottom: '1px solid #E2E8F0', cursor: 'pointer',
        '&:hover .tac-label': { color: '#1565C0' },
      }}>
      <Box sx={{
        width: 28, height: 14, borderRadius: 7, flexShrink: 0,
        backgroundColor: showTacPolygons ? '#1565C0' : '#CBD5E0',
        border: '1px solid ' + (showTacPolygons ? '#1565C0' : '#94A3B8'),
        position: 'relative', transition: 'all 0.15s',
      }}>
        <Box sx={{
          position: 'absolute', top: 1, left: showTacPolygons ? 15 : 1,
          width: 10, height: 10, borderRadius: '50%',
          backgroundColor: '#fff', transition: 'left 0.15s',
        }} />
      </Box>
      <Typography className="tac-label" sx={{
        fontFamily: 'inherit', fontSize: 9, fontWeight: 700,
        color: showTacPolygons ? '#1565C0' : '#94A3B8',
        textTransform: 'uppercase', letterSpacing: 0.8, transition: 'color 0.15s',
      }}>
        TAC Boundaries
      </Typography>
    </Box>

    {/* Active Only toggle */}
    <Box onClick={function() { onHideInactiveChange(!hideInactive); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1.2, pb: 1,
        borderBottom: '1px solid #E2E8F0', cursor: 'pointer',
        '&:hover .hide-label': { color: '#1565C0' },
      }}>
      <Box sx={{
        width: 28, height: 14, borderRadius: 7, flexShrink: 0,
        backgroundColor: hideInactive ? '#1565C0' : '#CBD5E0',
        border: '1px solid ' + (hideInactive ? '#1565C0' : '#94A3B8'),
        position: 'relative', transition: 'all 0.15s',
      }}>
        <Box sx={{
          position: 'absolute', top: 1, left: hideInactive ? 15 : 1,
          width: 10, height: 10, borderRadius: '50%',
          backgroundColor: '#fff', transition: 'left 0.15s',
        }} />
      </Box>
      <Typography className="hide-label" sx={{
        fontFamily: 'inherit', fontSize: 9, fontWeight: 700,
        color: hideInactive ? '#1565C0' : '#94A3B8',
        textTransform: 'uppercase', letterSpacing: 0.8, transition: 'color 0.15s',
      }}>
        Hide Inactive Sites
      </Typography>
    </Box>

    {/* Site counts */}
    {total > 0 && (
      <Box sx={{ mb: 1.4, pb: 1.2, borderBottom: '1px solid #E2E8F0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.8 }}>
          <Typography sx={{ fontFamily: 'inherit', fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>
            Sites
          </Typography>
          <Typography sx={{ fontFamily: 'inherit', fontSize: 13, fontWeight: 800, color: '#1565C0' }}>
            {total.toLocaleString()}
          </Typography>
        </Box>
        {colourMode === 'status' && [
          { key: 'inprogress', label: 'In Progress',     color: STATUS_COLORS.inprogress },
          { key: 'planned',    label: 'Planned',          color: STATUS_COLORS.planned    },
          { key: 'overdue',    label: 'Closed / Expired', color: STATUS_COLORS.overdue || '#BF360C' },
          { key: 'none',       label: 'No Activity',      color: STATUS_COLORS.none       },
        ].filter(function(x) { return stats[x.key] > 0; }).map(function(x) {
          return (
            <Box key={x.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.3 }}>
              <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#64748B', letterSpacing: 0.5 }}>{x.label}</Typography>
              <Typography sx={{ fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: x.color }}>{stats[x.key].toLocaleString()}</Typography>
            </Box>
          );
        })}
        {colourMode === 'feature' && Object.entries(featureStats).map(function(entry) {
          var name = entry[0]; var val = entry[1];
          return (
            <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.3 }}>
              <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#64748B', letterSpacing: 0.5 }}>{name}</Typography>
              <Typography sx={{ fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: val.color }}>{val.count.toLocaleString()}</Typography>
            </Box>
          );
        })}
      </Box>
    )}

    {/* Legend dots */}
    <Typography sx={{ fontFamily: 'inherit', fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1.2, display: 'block', mb: 1 }}>
      {colourMode === 'status' ? 'Activity Status' : 'Type'}
    </Typography>

    {colourMode === 'status' ? (
      (activityStatus === 'Closed' ? [
        { key: 'overdue', label: 'Closed / Expired', color: STATUS_COLORS.overdue || '#BF360C' },
        { key: 'none',    label: 'No Activity',      color: STATUS_COLORS.none    || '#546E7A' },
      ] : [
        { key: 'inprogress', label: 'In Progress', color: STATUS_COLORS.inprogress },
        { key: 'planned',    label: 'Planned',      color: STATUS_COLORS.planned    },
        { key: 'none',       label: 'No Activity',  color: STATUS_COLORS.none       },
      ]).map(function(x) {
        return (
          <Box key={x.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, backgroundColor: x.color,
                       boxShadow: x.key !== 'none' ? '0 0 5px ' + x.color : 'none' }} />
            <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#475569' }}>{x.label}</Typography>
          </Box>
        );
      })
    ) : Object.keys(featureStats).length === 0 ? (
      <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#94A3B8', fontStyle: 'italic' }}>No active types</Typography>
    ) : (
      Object.entries(featureStats).map(function(entry) {
        var name = entry[0]; var val = entry[1];
        return (
          <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                       backgroundColor: val.color, boxShadow: '0 0 5px ' + val.color }} />
            <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#475569',
                              maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </Typography>
          </Box>
        );
      }).concat(hideInactive ? [] : [
        <Box key="none" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, backgroundColor: STATUS_COLORS.none }} />
          <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#475569' }}>No Activity</Typography>
        </Box>
      ])
    )}

    {/* Logo */}
    <Box sx={{ mt: 1.2, pt: 1, borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'center' }}>
      <Box component="img" src={celfinetLogo} alt="Celfinet" sx={{ height: 36, width: 'auto' }} />
    </Box>
  </Box>
  );
};

MapLegend.defaultProps = { stats: {}, total: 0, featureStats: {}, hideInactive: false, activityStatus: 'Open' };

export default MapLegend;